// CANONICAL COPY (MNSA-01, the GH #7115 pattern). src/components/bia/biaReviews.js re-exports this file so the browser and the
// Function App share one definition. Edit here, never there.
'use strict';

/**
 * src/components/bia/biaReviews.js
 *
 * BIA reviews, review cycles and supplier declarations (BCR-03, TPS-05; msr
 * 4.5a, GH #7062). Mirrors azure-functions/shared/biaReviews/record.js and the
 * reading in azure-functions/shared/measurement/measures/bia.cjs — change one,
 * check the others.
 *
 * A review is an event the server stamps; the BIA's review is due one cycle
 * after the later of its last approval and its latest non-void review. The cycle
 * is set per BIA, 1–365 days, default 365; each change is appended, so the
 * latest setting is the one in force.
 */

const REVIEW_OUTCOMES = Object.freeze({
  confirmed: 'Confirmed — the analysis still holds',
  'issues-found': 'Issues found — the analysis needs rework',
});

const DEFAULT_REVIEW_CYCLE_DAYS = 365;
// measures/bia.cjs BIA_REVIEW_FROM: a completion before it carries no
// approvedAt and is read as an approval; one after it without approvedAt only
// re-closed an unchanged analysis (owner decision on PR #7412, question 2).
const BIA_REVIEW_FROM = '2026-09-30T00:00:00.000Z';
const MAX_REVIEW_CYCLE_DAYS = 365;
const DAY_MS = 24 * 60 * 60 * 1000;

const list = (state, key) => (Array.isArray(state?.[key]) ? state[key] : []);
const ms = (v) => {
  const t = Date.parse(v || '');
  return Number.isFinite(t) ? t : NaN;
};

const isVoidedReview = (r) => !!r && r.voidedAt != null;

/** Reviews of one BIA, newest first, voided included. */
function reviewsForBia(state, biaId) {
  return list(state, 'biaReviews')
    .filter((r) => r && r.biaId === biaId)
    .sort((a, b) => String(b.reviewedAt || '').localeCompare(String(a.reviewedAt || '')));
}

/** The cycle in force for a BIA: its latest setting, else 365 days, never more. */
function biaReviewCycleDays(state, biaId) {
  const latest = list(state, 'biaReviewCycles')
    .filter((c) => c && c.biaId === biaId)
    .sort((a, b) => String(a.setAt || '').localeCompare(String(b.setAt || '')))
    .pop();
  const d = latest ? latest.days : DEFAULT_REVIEW_CYCLE_DAYS;
  return Number.isInteger(d) && d >= 1
    ? Math.min(d, MAX_REVIEW_CYCLE_DAYS)
    : DEFAULT_REVIEW_CYCLE_DAYS;
}

/** True when the BIA has been completed at least once and is not archived. */
function isReviewable(bia) {
  if (!bia || bia.status === 'archived') return false;
  return bia.status === 'completed' || !Number.isNaN(ms(bia.completedAt));
}

/**
 * Where a BIA stands against its cycle:
 *   { applies, lastAt, lastKind ('review'|'approval'|null), cycleDays,
 *     dueAt, overdue, legacy }
 * The approval is approvedAt, stamped only when a completion approved a
 * first or changed analysis — reopening and completing again with nothing
 * changed is not a review. `legacy` — no approval or review on record: it is
 * due now.
 */
function biaReviewStatus(state, bia, now = Date.now()) {
  const cycleDays = biaReviewCycleDays(state, bia?.id);
  if (!isReviewable(bia)) return { applies: false, cycleDays };
  const review = reviewsForBia(state, bia.id).find((r) => !isVoidedReview(r));
  const reviewMs = ms(review?.reviewedAt);
  const approvedMs = ms(bia.approvedAt);
  const completedMs = ms(bia.completedAt);
  const approvalMs = !Number.isNaN(approvedMs)
    ? approvedMs
    : !Number.isNaN(completedMs) && completedMs <= ms(BIA_REVIEW_FROM)
      ? completedMs
      : NaN;
  const candidates = [
    ['review', reviewMs],
    ['approval', approvalMs],
  ].filter(([, t]) => !Number.isNaN(t));
  if (!candidates.length) {
    return {
      applies: true,
      lastAt: null,
      lastKind: null,
      cycleDays,
      dueAt: null,
      overdue: true,
      legacy: true,
    };
  }
  const [lastKind, lastMs] = candidates.sort((a, b) => b[1] - a[1])[0];
  const dueMs = lastMs + cycleDays * DAY_MS;
  return {
    applies: true,
    lastAt: new Date(lastMs).toISOString(),
    lastKind,
    cycleDays,
    dueAt: new Date(dueMs).toISOString(),
    overdue: now > dueMs,
    legacy: false,
  };
}

/** Client-side check of a review draft; the server applies the same rules. */
function reviewErrors(draft) {
  const errs = [];
  if (!draft || !REVIEW_OUTCOMES[draft.outcome]) errs.push('Choose an outcome.');
  if (!draft?.note || !draft.note.trim()) errs.push('Say what the review looked at.');
  return errs;
}

function isValidCycleDays(v) {
  const n = Number(v);
  return (
    /^\d+$/.test(String(v).trim()) && Number.isInteger(n) && n >= 1 && n <= MAX_REVIEW_CYCLE_DAYS
  );
}

/** The BIA's upstream rows that name an external supplier (BIAWizard vendor/service). */
function supplierDependencies(bia) {
  const up =
    bia?.dependencies && Array.isArray(bia.dependencies.upstream) ? bia.dependencies.upstream : [];
  return up.filter(
    (d) => d && (d.refType === 'vendor' || d.type === 'vendor' || d.type === 'service')
  );
}

/** The latest supplier declaration of a BIA, or null. */
function latestSupplierDeclaration(state, biaId) {
  return (
    list(state, 'biaSupplierDeclarations')
      .filter((d) => d && d.biaId === biaId)
      .sort((a, b) => String(a.declaredAt || '').localeCompare(String(b.declaredAt || '')))
      .pop() || null
  );
}

/** The findings-register prefill for a review that found issues. */
function reviewFindingPrefill(review, bia) {
  const name = bia?.assetName || bia?.id || 'BIA';
  return {
    title: `BIA review found issues: ${name}`.slice(0, 120),
    description: `The review of the business impact analysis for "${name}" found issues.\n\n${review.note || ''}`,
    source: 'other',
    sourceRef: review.id,
    severity: 'minor',
  };
}

/** The finding already raised from a review, if any. */
function findingForReview(findings, reviewId) {
  return (Array.isArray(findings) ? findings : []).find(
    (f) => f && f.source === 'other' && f.sourceRef === reviewId
  );
}

/** Completed BIAs whose review is due, for the owner (or, unowned, an admin). */
function biaReviewsDueFor(state, userId, isAdmin, ownerMatches, now = Date.now()) {
  return list(state, 'biaAssessments').filter((b) => {
    const st = biaReviewStatus(state, b, now);
    if (!st.applies || !st.overdue) return false;
    if (b.owner) return ownerMatches(b.owner, userId) || false;
    return !!isAdmin;
  });
}

module.exports = {
  REVIEW_OUTCOMES,
  DEFAULT_REVIEW_CYCLE_DAYS,
  BIA_REVIEW_FROM,
  MAX_REVIEW_CYCLE_DAYS,
  isVoidedReview,
  reviewsForBia,
  biaReviewCycleDays,
  isReviewable,
  biaReviewStatus,
  reviewErrors,
  isValidCycleDays,
  supplierDependencies,
  latestSupplierDeclaration,
  reviewFindingPrefill,
  findingForReview,
  biaReviewsDueFor,
};
